import React, { useEffect, useRef } from "react";
import {
	Box,
	List,
	ListItemButton,
	ListItemText,
	TextField,
	Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

const OverrideDot = ({ visible }) => (
	<Box
		sx={{
			width: 8,
			height: 8,
			borderRadius: "50%",
			flexShrink: 0,
			ml: 1,
			bgcolor: visible ? "primary.main" : "transparent",
		}}
	/>
);

const TermList = ({ terms, selectedKey, onSelect, query, onQueryChange }) => {
	const { t } = useTranslation();
	const listRef = useRef(null);
	const itemRefs = useRef({});

	useEffect(() => {
		itemRefs.current[selectedKey]?.scrollIntoView?.({ block: "nearest" });
	}, [selectedKey]);

	const moveSelection = (offset) => {
		if (terms.length === 0) return;

		const currentIndex = terms.findIndex((t) => t.key === selectedKey);
		const nextIndex =
			currentIndex === -1
				? 0
				: Math.min(Math.max(currentIndex + offset, 0), terms.length - 1);

		onSelect(terms[nextIndex].key);
	};

	const handleListKeyDown = (e) => {
		if (e.key === "ArrowDown") {
			e.preventDefault();
			moveSelection(1);
		} else if (e.key === "ArrowUp") {
			e.preventDefault();
			moveSelection(-1);
		} else if (e.key === "Home") {
			e.preventDefault();
			if (terms.length > 0) onSelect(terms[0].key);
		} else if (e.key === "End") {
			e.preventDefault();
			if (terms.length > 0) onSelect(terms[terms.length - 1].key);
		}
	};

	const handleSearchKeyDown = (e) => {
		if (e.key === "ArrowDown") {
			e.preventDefault();
			if (terms.length > 0 && !selectedKey) onSelect(terms[0].key);
			listRef.current?.focus();
		}
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "100%",
				minHeight: 0,
			}}
		>
			<TextField
				size="small"
				fullWidth
				placeholder={t("placeholder.searchTerms")}
				value={query}
				onChange={(e) => onQueryChange(e.target.value)}
				onKeyDown={handleSearchKeyDown}
				inputProps={{ "aria-label": "Search terms" }}
				sx={{ mb: 1 }}
			/>

			{terms.length === 0 ? (
				<Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
					{t("message.noTermsMatchFilters")}
				</Typography>
			) : (
				<List
					dense
					ref={listRef}
					tabIndex={0}
					onKeyDown={handleListKeyDown}
					aria-label="Terminology keys"
					sx={{
						overflowY: "auto",
						flexGrow: 1,
						minHeight: 0,
						// Scroll inside the pane instead of growing the page,
						// so the editor on the right stays in view.
						maxHeight: "max(320px, calc(100vh - 380px))",
						outline: "none",
						border: 1,
						borderColor: "divider",
						borderRadius: 1,
					}}
				>
					{terms.map((term) => (
						<ListItemButton
							key={term.key}
							ref={(node) => {
								itemRefs.current[term.key] = node;
							}}
							selected={term.key === selectedKey}
							onClick={() => onSelect(term.key)}
						>
							<ListItemText
								primary={term.effectiveSingular}
								secondary={term.key}
								primaryTypographyProps={{ noWrap: true }}
								secondaryTypographyProps={{ noWrap: true }}
							/>
							<OverrideDot visible={term.isOverridden} />
						</ListItemButton>
					))}
				</List>
			)}
		</Box>
	);
};

export default TermList;
